// use-set-team-model-mode.ts — Teams suite, action
//
// POST /api/teams/:teamId/model-mode. Flips the team between its economical and
// performance model modes and returns the refreshed TeamDetail.

'use client';

import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { TeamModelMode } from '@cabal/shared/domain/session/team/enums.js';
import type { TeamDetail } from '@cabal/shared/domain/session/team/types.js';
import { useApi } from '@/contexts/api';
import { useActionHook } from '@/lib/query/use-action-hook';
import type { ActionResult } from '@/types/async-protocol';
import { teamsDetailKey, teamsRootKey } from './team-query-keys';

export interface SetTeamModelModeVars {
  teamId: string;
  mode: TeamModelMode;
}

export function useSetTeamModelMode(): ActionResult<SetTeamModelModeVars, TeamDetail> {
  const api = useApi();
  const queryClient = useQueryClient();

  return useActionHook<SetTeamModelModeVars, TeamDetail>(
    useMemo(
      () => ({
        mutationFn: ({ teamId, mode }) =>
          api.teams.byId(teamId).setModelMode({ body: { mode } }),
        onInvalidate: (_data, { teamId }) => {
          queryClient.invalidateQueries({ queryKey: teamsDetailKey(teamId) });
          queryClient.invalidateQueries({ queryKey: teamsRootKey });
        },
      }),
      [api, queryClient],
    ),
  );
}
